import { Avatar } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ArrowRight } from 'lucide-react';

/**
 * Train Me submission: who submitted it, their note, the inline current → proposed
 * diff (struck-through current, amber proposal) and the source record. Flat flush
 * section; partition and version ride under the record as secondary metadata.
 */
function SubmissionBlock({ detail }) {
  const { t } = useTranslation('work-queues-app');
  if (!detail) return null;
  const { submitter, note, submittedAt, currentValue, proposedValue, sourceRecord } = detail;

  const submitterMeta = [submitter?.role, submittedAt].filter(Boolean).join(' · ');
  const recordMeta = [detail.partition, detail.version].filter(Boolean).join(' · ');

  let valueDiff = null;
  if (currentValue && proposedValue) {
    const compact =
      String(currentValue).length <= 24 && String(proposedValue).length <= 24;
    const valueClass = compact
      ? 'mt-1 text-xl font-semibold'
      : 'mt-1 break-words text-[13px] font-semibold leading-relaxed';
    valueDiff = (
      <div className="mt-4 flex items-end gap-5">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-content-faint">{t('trainMe.currentValue')}</p>
          <p className={`${valueClass} text-content-faint line-through`}>{currentValue}</p>
        </div>
        <ArrowRight size={18} className="mb-2 shrink-0 text-content-faint" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-content-faint">{t('trainMe.proposedValue')}</p>
          <p className={`${valueClass} text-amber-500`}>{proposedValue}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-content-faint">
        {t('trainMe.submission')}
      </p>

      {submitter && (
        <div className="mt-3 flex items-center gap-2">
          <Avatar size="sm" initials={submitter.initials} alt={submitter.name} />
          <div>
            <p className="text-sm font-medium text-content">{submitter.name}</p>
            {submitterMeta && <p className="text-xs text-content-faint">{submitterMeta}</p>}
          </div>
        </div>
      )}
      {note && <p className="mt-3 text-sm text-content-secondary">{note}</p>}

      {valueDiff}

      {!currentValue && proposedValue && (
        <div className="mt-4">
          <p className="text-xs text-content-faint">{t('trainMe.proposedValue')}</p>
          <p
            className={
              String(proposedValue).length <= 24
                ? 'mt-1 text-xl font-semibold text-amber-500'
                : 'mt-1 break-words text-[13px] font-semibold leading-relaxed text-amber-500'
            }
          >
            {proposedValue}
          </p>
        </div>
      )}

      {sourceRecord && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-content-faint">
            {t('trainMe.sourceRecord')}
          </p>
          <p className="mt-1 text-sm text-content">{sourceRecord}</p>
          {recordMeta && <p className="mt-0.5 text-xs text-content-faint">{recordMeta}</p>}
        </div>
      )}
    </div>
  );
}

export default SubmissionBlock;
